import { makeStyles, Typography } from '@material-ui/core';
import {
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  /*
   * Wrap the `<img>` in a figure-like container so an optional caption
   * sits flush against the image with consistent gutters across pages.
   * The thin border + rounded corners match the InfoCard chrome used
   * elsewhere so embedded screenshots / diagrams read as "another
   * card on this page".
   */
  figure: {
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${boschGray[90]}`,
    borderRadius: 6,
    overflow: 'hidden',
    padding: theme.spacing(1.5),
  },
  img: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 4,
  },
  caption: {
    fontSize: '0.8rem',
    color: boschGray[40],
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: theme.spacing(1),
    maxWidth: '70ch',
    lineHeight: 1.45,
  },
  error: {
    padding: theme.spacing(2),
    fontSize: '0.85rem',
    color: boschGray[40],
    fontStyle: 'italic',
  },
}));

export type ImageBlockProps = {
  src: string;
  alt?: string;
  caption?: string;
  maxWidth?: number;
};

/*
 * Image sources are constrained for the same reason iframes are:
 * an admin pasting an arbitrary URL becomes a way to serve hostile
 * content (tracking pixels, malware) from a trusted origin. For
 * images the threat surface is smaller (no JS), but a quick
 * allow-list keeps the principle consistent.
 *
 * Allowed schemes:
 *   - `https://` to any host (read-only payload, no auth bleed)
 *   - `data:image/...` for inline base64 diagrams (admin can paste
 *     a screenshot directly without uploading anywhere)
 *
 * Disallowed:
 *   - `http://` (no plaintext fetches; corp CSP would block anyway)
 *   - `file://`, `javascript:`, and any non-http scheme
 */
const isSafeImageSrc = (src: string): boolean => {
  if (src.startsWith('data:image/')) return true;
  try {
    const url = new URL(src);
    return url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Inline image widget with an optional caption.
 *
 * Sized at `maxWidth` (defaults to 720px so a diagram fits comfortably
 * even on a 1366px laptop next to the sidebar). The wrapper centres
 * the image so portrait-orientation screenshots don't drift to the
 * left of the page; landscape shots fill the full content width up
 * to `maxWidth`.
 *
 * Caption is rendered in italic mono-style (`Typography variant="body2"`)
 * to match the existing visual treatment of caption-like rows on
 * `KindsGuidePage` and the home dashboard.
 */
export const ImageBlock = ({
  src,
  alt = '',
  caption,
  maxWidth = 720,
}: ImageBlockProps) => {
  const classes = useStyles();

  if (!isSafeImageSrc(src)) {
    return (
      <div className={classes.figure}>
        <Typography className={classes.error}>
          Image src must be an HTTPS URL or a <code>data:image/…</code>{' '}
          payload. Got: <code>{src}</code>.
        </Typography>
      </div>
    );
  }

  return (
    <figure className={classes.figure}>
      <img
        src={src}
        alt={alt}
        className={classes.img}
        style={{ maxWidth }}
      />
      {caption && (
        <Typography component="figcaption" className={classes.caption}>
          {caption}
        </Typography>
      )}
    </figure>
  );
};
